import { EventEmitter } from "eventemitter3";
import { AudioService } from "../audio/service";
import { LatencyEstimator } from "../audio/latency_estimator";
import { AgentConfig, AgentState, ClientEvents, ClientInterface, Config } from "./types";

// const HOST = "wss://gateway.millis.ai:8080/";
// const HOST = "ws://54.177.98.36:8080/";
// const HOST = "ws://betaapi.tmatehq.com:8080/";
const HOST = "wss://api-west.millis.ai:8080/millis";
// const HOST = "ws://localhost:8080/millis";

export interface IMessage {
  method: string;
  data?: string;
  payload?: any;
}

export interface IStartPayload {
  // call_id?: string,
  agent: {
    agent_id?: string,
    agent_config?: AgentConfig,
  },
  metadata?: object,
  include_metadata_in_prompt?: boolean,
  session_continuation?: {
    session_id?: string
  }
}

enum State {
  IDLE,
  READY,
  GOT_READY_EVENT,
  GOT_FIRST_AUDIO
}

export class Client extends EventEmitter<ClientEvents> implements ClientInterface {
  private config: Config;
  private ws: WebSocket | null;
  private audioService: AudioService | null;
  private startAnswering: number = 0;
  private count: number = 0;
  private latencyEstimator: LatencyEstimator | null;
  private agentState: AgentState = AgentState.IDLE;
  private state: State = State.IDLE;
  private muted: boolean = false;
  private readyPayload: object | undefined = undefined;

  constructor(config: Config) {
    super();
    this.ws = null;
    this.audioService = null;
    this.latencyEstimator = null;
    const defaultConfig = {
      debug: false
    }
    this.config = {...defaultConfig, ...config};
    this.print('init');
  }

  private reset() {
    this.state = State.IDLE;
    this.agentState = AgentState.IDLE;
    this.count = 0;
    this.startAnswering = 0;
    this.muted = false;
  }

  private print(message: string) {
    if (this.config.debug) {
      console.log("[millis]", message);
    }
  }

  private connect(payload: IStartPayload) {
    this.print('starting websocket');
    this.ws = new WebSocket(this.config.endPoint || HOST);
    this.ws.binaryType = "arraybuffer";

    this.ws.onopen = () => {
      this.print('websocket connected, sending initiate message');
      this.emit("onopen");
      this.send(JSON.stringify({
        "method": "initiate",
        "data": {
          public_key: this.config.publicKey,
          agent: payload.agent,
          metadata: payload.metadata,
          include_metadata_in_prompt: payload.include_metadata_in_prompt,
          session_continuation: payload.session_continuation
        }}));
    };

    this.ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        this.print('audio data received');
        // this.config.isTest && this.latencyEstimator?.onAudio();
        if (this.startAnswering > 0) {
          this.emit("onlatency", Date.now() - this.startAnswering);
          this.print('latency: ' + (Date.now() - this.startAnswering));
          this.startAnswering = 0;
          this.switchState(AgentState.ANSWER);
        }
        if (!this.config.isBenchmark) {
          const audio = new Uint8Array(event.data);
          this.audioService?.play(audio);
          this.emit("onaudio", audio);
        }
      } else {
        const message: IMessage = JSON.parse(event.data);
        if (message.method === "onready") {
          this.onready(message.payload);
          return;
        }
        if (!this.handle(message)) {
          this.emit(message.method as keyof ClientEvents, message.data as any, message.payload);
        }
        this.print(`received ${message.method}`);
      }
    };
    this.ws.onclose = (event: CloseEvent) => {
      this.stop();
      this.emit("onclose", event);
      this.print('websocket disconnected');
      this.switchState(AgentState.IDLE);
    };
    this.ws.onerror = (event: Event) => {
      this.stop();
      this.emit("onerror", event);
      this.print('websocket error: ' + event);
    };
  }

  public send(data: Uint8Array | string) {
    if (this.ws?.readyState === 1) {
      this.ws.send(data);
    }
  }

  private handle(message: IMessage) {
    switch(message.method) {
      case "start_answering":
        this.startAnswering = Date.now();
        this.switchState(AgentState.PREPARE_ANSWER);
        return true;
      case "clear":
        this.audioService?.reset();
        this.switchState(AgentState.IDLE);
        return true;
      case "pause":
        this.audioService?.setpause(true);
        this.switchState(AgentState.PAUSE);
        return true;
      case "unpause":
        this.audioService?.setpause(false);
        this.switchState(AgentState.ANSWER);
        return true;
      case "pong":
        this.print("Ping rtt " + (Date.now() - Number(message.data)))
        return true;
    }
    return false;
  }

  private onready(payload?: object) {
    // if (this.config.isTest) {
    //   this.latencyEstimator = new LatencyEstimator(this);
    //   this.latencyEstimator.start();
    // }
    this.readyPayload = payload;
    this.switchState(AgentState.IDLE);
    this.switchConnectionState(State.GOT_READY_EVENT);
  }

  public async start(payload: IStartPayload | AgentConfig | string, metadata?: object, includeMetadataInPrompt?: boolean) {
    this.print('starting conversation');
    this.audioService = new AudioService(this.config);
    this.audioService.on("data", (audio: Uint8Array) => {
      // if (this.config.isTest && this.latencyEstimator?.isTesting()) {
      //   return;
      // }
      if (this.muted) {
        audio.fill(0);
      } else {
        this.print('sending audio data');
      }
      this.send(audio);
      this.count++;
      if (this.count % 1000 == 0) {
        this.print('sending ping');
        this.send(JSON.stringify({"method": "ping", "data": Date.now().toString()}));
      }
      this.switchConnectionState(State.GOT_FIRST_AUDIO);
    });
    this.audioService.on("analyzer", (analyzer) => {
      this.emit("analyzer", analyzer);
    });
    this.audioService.on("useraudioready", (analyzer) => {
      this.print('user audio ready');
      this.emit("useraudioready", analyzer);
    });
    this.audioService.on("playback_finished", () => {
      if (this.agentState === AgentState.ANSWER) {
        this.switchState(AgentState.IDLE);
      }
    })

    this.print('starting audio service');
    await this.audioService.start();
    this.print('audio service started');
    let finalPayload: IStartPayload;
    if (typeof payload === "object" && ('agent' in payload || 'call_id' in payload)) {
      finalPayload = payload as IStartPayload;
    } else {
      finalPayload = {agent: typeof payload === "string" ? {agent_id: payload} : {agent_config: payload as AgentConfig}};
      if (metadata) {
        finalPayload.metadata = metadata;
      }
      if (includeMetadataInPrompt) {
        finalPayload.include_metadata_in_prompt = includeMetadataInPrompt;
      }
    }
    this.connect(finalPayload);
  }

  public async stop() {
    if (this.audioService) {
      this.print('stopping audio service');
      this.audioService.stop();
      this.audioService = null;
      this.print('audio service stopped');
    }
    this.ws?.close();
    this.reset();
  }

  public mute() {
    this.muted = true;
  }

  public unmute() {
    this.muted = false;
  }

  private switchState(state: AgentState) {
    this.agentState = state;
    this.emit("onagentstate", state);
  }

  private switchConnectionState(newState: State) {
    if (this.state === State.IDLE) {
      this.state = newState;
    } else if ((this.state === State.GOT_FIRST_AUDIO && newState === State.GOT_READY_EVENT)
      || (this.state === State.GOT_READY_EVENT && newState === State.GOT_FIRST_AUDIO)) {
      this.state = State.READY;
      this.emit("onready", this.readyPayload);
    }
  }
}
